import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  generateSlotKeys,
  formatSlotKey,
  getPlayerAvailability,
  savePlayerAvailability,
} from '../services/playerAvailabilityService';
import { setCalendarConnected, getCalendarConnected } from '../services/userCalendarSettingsService';
import { migrateScheduleConfig } from '../utils/scheduleStrategy';
import './PlayerAvailabilityModal.css';

export default function PlayerAvailabilityModal({
  tournament,
  playerId,
  onClose,
  onSaved,
}) {
  const config = migrateScheduleConfig(tournament?.scheduleConfig || {});
  const slotKeys = generateSlotKeys(config);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [exclusionDates, setExclusionDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [newExclusionDate, setNewExclusionDate] = useState('');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const { requestCalendarAccess } = useAuth();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!tournament?.id || !playerId) return;
      setLoading(true);
      try {
        const data = await getPlayerAvailability(tournament.id, playerId);
        if (cancelled) return;
        if (data) {
          setSelectAll(data.selectAll);
          setSelectedSlots(new Set(data.availableSlots || []));
          setExclusionDates(data.exclusionDates || []);
          setHolidayDates(data.holidayDates || []);
          setGoogleCalendarConnected(data.googleCalendarConnected || false);
        }
        const globalConnected = await getCalendarConnected(playerId);
        if (!cancelled && globalConnected) setGoogleCalendarConnected(true);
      } catch (err) {
        console.error('[PlayerAvailabilityModal] load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tournament?.id, playerId]);

  function handleSlotToggle(key) {
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSelectAllChange(checked) {
    setSelectAll(checked);
    if (checked) {
      setSelectedSlots(new Set(slotKeys));
    }
  }

  function addExclusionDate() {
    const d = newExclusionDate.trim();
    if (!d || exclusionDates.includes(d)) return;
    setExclusionDates((prev) => [...prev, d].sort());
    setNewExclusionDate('');
  }

  function removeExclusionDate(d) {
    setExclusionDates((prev) => prev.filter((x) => x !== d));
  }

  function addHolidayDate() {
    const d = newHolidayDate.trim();
    if (!d || holidayDates.includes(d)) return;
    setHolidayDates((prev) => [...prev, d].sort());
    setNewHolidayDate('');
  }

  function removeHolidayDate(d) {
    setHolidayDates((prev) => prev.filter((x) => x !== d));
  }

  async function handleSave() {
    if (!tournament?.id || !playerId) return;
    setSaving(true);
    try {
      await savePlayerAvailability(tournament.id, playerId, {
        selectAll,
        availableSlots: selectAll ? slotKeys : [...selectedSlots],
        exclusionDates,
        holidayDates,
        googleCalendarConnected,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[PlayerAvailabilityModal] save failed', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleGoogleCalendarConnect() {
    try {
      await requestCalendarAccess(true); // first-time connect: show consent once
      setGoogleCalendarConnected(true);
      if (playerId) await setCalendarConnected(playerId, true);
      if (tournament?.id && playerId) {
        await savePlayerAvailability(tournament.id, playerId, {
          selectAll,
          availableSlots: selectAll ? slotKeys : [...selectedSlots],
          exclusionDates,
          holidayDates,
          googleCalendarConnected: true,
        });
      }
    } catch (err) {
      console.warn('[PlayerAvailabilityModal] Calendar connect failed', err);
    }
  }

  if (loading) {
    return (
      <div className="player-availability-overlay" onClick={onClose}>
        <div className="player-availability-modal" onClick={(e) => e.stopPropagation()}>
          <div className="player-availability-loading">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="player-availability-overlay" onClick={onClose}>
      <div className="player-availability-modal" onClick={(e) => e.stopPropagation()}>
        <div className="player-availability-header">
          <h2>Your availability</h2>
          <p className="player-availability-subtitle">
            {tournament?.name} – select when you can play matches
          </p>
          <button type="button" className="player-availability-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="player-availability-body">
          <div className="player-availability-section">
            <label className="checkbox-label player-availability-select-all">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAllChange(e.target.checked)}
              />
              Select all slots (available for all scheduled times)
            </label>
          </div>

          {!selectAll && (
            <div className="player-availability-section">
              <h3>Available time slots (by hour)</h3>
              <p className="player-availability-hint">
                Select each slot when you are available. Tournament uses: {config.scheduleStart}–{config.scheduleEnd}, {config.matchDuration} min matches.
              </p>
              <div className="player-availability-slots-grid">
                {slotKeys.map((key) => (
                  <label key={key} className="player-availability-slot-chip">
                    <input
                      type="checkbox"
                      checked={selectedSlots.has(key)}
                      onChange={() => handleSlotToggle(key)}
                    />
                    <span>{formatSlotKey(key)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="player-availability-section">
            <h3>Exclusion days</h3>
            <p className="player-availability-hint">Dates when you are not available (e.g. travel, events).</p>
            <div className="player-availability-date-input-row">
              <input
                type="date"
                value={newExclusionDate}
                onChange={(e) => setNewExclusionDate(e.target.value)}
                className="player-availability-date-input"
              />
              <button type="button" className="btn-secondary" onClick={addExclusionDate}>Add</button>
            </div>
            <div className="player-availability-date-list">
              {exclusionDates.map((d) => (
                <span key={d} className="player-availability-date-tag">
                  {d}
                  <button type="button" onClick={() => removeExclusionDate(d)} aria-label={`Remove ${d}`}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="player-availability-section">
            <h3>Holidays / special dates</h3>
            <p className="player-availability-hint">Dates you need off (e.g. public holidays, personal).</p>
            <div className="player-availability-date-input-row">
              <input
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                className="player-availability-date-input"
              />
              <button type="button" className="btn-secondary" onClick={addHolidayDate}>Add</button>
            </div>
            <div className="player-availability-date-list">
              {holidayDates.map((d) => (
                <span key={d} className="player-availability-date-tag">
                  {d}
                  <button type="button" onClick={() => removeHolidayDate(d)} aria-label={`Remove ${d}`}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="player-availability-section">
            <h3>Google Calendar</h3>
            <p className="player-availability-hint">
              Connect your Google Calendar so we can check your availability and add match events.
            </p>
            {googleCalendarConnected ? (
              <p className="player-availability-connected">✓ Google Calendar connected</p>
            ) : (
              <button type="button" className="btn-secondary" onClick={handleGoogleCalendarConnect}>
                Connect Google Calendar
              </button>
            )}
          </div>
        </div>

        <div className="player-availability-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save availability'}
          </button>
        </div>
      </div>
    </div>
  );
}
